export const moneyValue = (value) => Number(String(value ?? 0).replace(/[^\d.-]/g, '')) || 0;
const cents = (value) => Math.round(moneyValue(value) * 100);
export const money = (value) => `₹ ${moneyValue(value).toFixed(2)}`;
export const isEnabled = (value) => value === true || value === 1 || value === '1' || value === 'true';

export function durationNames(plan) {
  return [...new Set((plan.month_price || []).map((row) => row.pricing_duration_name).filter(Boolean))];
}

export function pricedModules(plan, duration) {
  return (plan.modules || []).filter((module) =>
    (plan.month_price || []).some((row) =>
      Number(row.quest_category_id) === Number(module.quest_category_id) &&
      row.pricing_duration_name === duration && Number(row.pricing_duration_id) > 0));
}

export function modulesForDuration(plan, duration, modules) {
  const available = new Set(pricedModules(plan, duration).map((module) => String(module.quest_category_id)));
  return Object.fromEntries(Object.entries(modules).map(([id, selected]) => [id, selected && available.has(String(id))]));
}

export function createCart(plans, subjectId, categoryId) {
  return (plans || []).map((plan) => {
    const selected = Number(subjectId) > 0 && Number(plan.subject_id) === Number(subjectId);
    const modules = Object.fromEntries((plan.modules || []).map((item) => [
      item.quest_category_id,
      selected && Number(categoryId) > 0
        ? Number(item.quest_category_id) === Number(categoryId)
        : isEnabled(item.is_selected),
    ]));
    const duration = (selected && Number(categoryId) > 0
      ? durationNames(plan).find((name) => pricedModules(plan, name).some((module) => Number(module.quest_category_id) === Number(categoryId)))
      : '') || durationNames(plan)[0] || '';
    return { plan, selected, duration, modules: modulesForDuration(plan, duration, modules) };
  });
}

export function cartLine(item) {
  const selectedIds = Object.entries(item.modules || {}).filter(([, selected]) => Boolean(selected)).map(([id]) => Number(id));
  const rows = selectedIds.map((id) => (item.plan.month_price || []).find((row) =>
    Number(row.quest_category_id) === id && row.pricing_duration_name === item.duration && Number(row.pricing_duration_id) > 0));
  const valid = selectedIds.length > 0 && rows.every((row) => row && Number.isFinite(Number(row.discounted_price)) && moneyValue(row.discounted_price) > 0 && Number.isFinite(Number(row.actual_price)))
    && new Set(rows.map((row) => Number(row.pricing_duration_id))).size === 1;
  const pricedRows = rows.filter(Boolean);
  return {
    actual: pricedRows.reduce((sum, row) => sum + cents(row.actual_price), 0) / 100,
    discounted: pricedRows.reduce((sum, row) => sum + cents(row.discounted_price), 0) / 100,
    valid,
    categories: valid ? pricedRows.map((row) => ({
      quest_category_id: Number(row.quest_category_id),
      pricing_duration_id: Number(row.pricing_duration_id),
    })) : [],
  };
}

export function cartTotals(cart) {
  return cart.filter((item) => item.selected).reduce((totals, item) => {
    const line = cartLine(item);
    return { actual: (cents(totals.actual) + cents(line.actual)) / 100, discounted: (cents(totals.discounted) + cents(line.discounted)) / 100 };
  }, { actual: 0, discounted: 0 });
}

export function cartValidationMessage(cart) {
  const selected = cart.filter((item) => item.selected);
  if (!selected.length) return 'Select at least one subject.';
  for (const item of selected) {
    const line = cartLine(item);
    if (Number(item.plan.subscription_id) <= 0 || !line.valid || !line.categories.length) {
      return `Pricing is unavailable for the selected content in ${item.plan.subject_name || 'this subject'} (${item.duration || 'no duration'}). Choose another duration or content type.`;
    }
  }
  return '';
}

export function validCart(cart) {
  return !cartValidationMessage(cart);
}

export function normalizeCartDuration(cart, duration) {
  return cart.map((item) => item.selected && durationNames(item.plan).includes(duration)
    ? { ...item, duration } : item);
}

export function changeCartItemDuration(cart, subscriptionId, duration) {
  return cart.map((item) => Number(item.plan.subscription_id) === Number(subscriptionId) && durationNames(item.plan).includes(duration)
    ? { ...item, duration } : item);
}

export function subjectOrderPayload(cart, stateId, user, amount) {
  const validationMessage = cartValidationMessage(cart);
  if (validationMessage) throw new Error(validationMessage);
  return {
    type: 'subject-subscriptions',
    order_type: 'subject-subscriptions',
    states_id: Number(stateId) || 0,
    subscriptions: cart.filter((item) => item.selected).map((item) => ({
      subscription_id: Number(item.plan.subscription_id),
      categories: cartLine(item).categories,
    })),
    amount: moneyValue(amount).toFixed(2),
    buyer_name: user?.name || user?.full_name || '',
    email: user?.email || '',
    phone: user?.phone || user?.mobile || user?.phone_number || '',
  };
}
